import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, LockOpen, Lock, Timer } from "lucide-react";
import { FadeUp } from "../Reveal";
import { blog as allPosts } from "../../data/content";

/* ─── Resolve access label & style ─────────────────────────── */
function getAccess(post) {
  const raw = post.access || (
    post.lockMode === "unlocked" ? "open" :
    post.lockMode === "locked"   ? "members" :
    post.lockMode === "timer"    ? "scheduled" : "open"
  );
  if (raw === "scheduled" && post.unlockAt) {
    if (Date.now() >= new Date(post.unlockAt).getTime()) return "open";
  }
  return raw;
}

function AccessChip({ post }) {
  const access = getAccess(post);
  if (access === "open") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-green-500/30 bg-green-500/10 px-2 py-[2px] font-mono text-[9px] tracking-[0.14em] uppercase text-green-400">
        <LockOpen className="w-2.5 h-2.5" />
        Free
      </span>
    );
  }
  if (access === "members") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-magenta-500/30 bg-magenta-500/10 px-2 py-[2px] font-mono text-[9px] tracking-[0.14em] uppercase text-magenta-300">
        <Lock className="w-2.5 h-2.5" />
        Members
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-[2px] font-mono text-[9px] tracking-[0.14em] uppercase text-amber-300">
      <Timer className="w-2.5 h-2.5" />
      Soon
    </span>
  );
}

function parseDateISO(iso) {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : 0;
}

/* ─── Blog (homepage section  -  redesigned, no modal) ───────── */
export const Blog = () => {
  // Sort newest first by dateISO. `latest` is simply the most recent post,
  // regardless of access status  -  so uploading a new .md file always updates
  // this card. The access chip on the card tells the visitor if it's open,
  // members-only, or scheduled.
  const sorted = allPosts
    .slice()
    .sort((a, b) => parseDateISO(b.dateISO) - parseDateISO(a.dateISO));
  const latest = sorted[0];
  const count = sorted.length;

  return (
    <section id="blog" className="relative py-20 sm:py-28 border-t border-edge-650">
      <div className="container-page">
        {/* ── Section header ── */}
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <FadeUp>
              <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-magenta-300">
                Writing
              </div>
            </FadeUp>
            <FadeUp delay={0.05}>
              <h2 className="mt-4 font-display tracking-tight text-3xl sm:text-5xl text-white max-w-3xl leading-[1.05]">
                Half-finished thoughts,<br className="hidden sm:inline" /> shipped on purpose.
              </h2>
            </FadeUp>
          </div>
          <FadeUp delay={0.08}>
            <Link
              to="/blog"
              data-testid="home-blog-view-all"
              className="inline-flex items-center gap-2 rounded-full border border-edge-700 px-4 py-2 text-xs font-mono tracking-[0.14em] uppercase text-zinc-300 hover:text-magenta-300 hover:border-magenta-500/40 transition-colors"
            >
              View all essays
              <ArrowRight className="w-3 h-3" />
            </Link>
          </FadeUp>
        </div>

        {/* ── Latest article card ── */}
        {latest && (
          <FadeUp delay={0.1}>
            <Link
              to={`/blog/${latest.slug}`}
              data-testid="home-blog-latest-card"
              className="mt-10 group block rounded-2xl border border-edge-700 bg-graphite-900/40 overflow-hidden hover:border-magenta-500/40 transition-colors"
            >
              <div className="grid grid-cols-1 md:grid-cols-[260px_1fr]">
                {/* Left: cover image */}
                <div
                  className="relative aspect-video md:aspect-auto md:min-h-full overflow-hidden"
                  style={{ background: latest.imageBg || "#1e2a30" }}
                >
                  {latest.coverImage ? (
                    <img
                      src={latest.coverImage}
                      alt={latest.title}
                      className="absolute inset-0 w-full h-full object-cover"
                      style={{ filter: "contrast(1.1) brightness(0.78) saturate(0.80)" }}
                    />
                  ) : (
                    <div className="absolute inset-0 bg-grid opacity-[0.15]" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent to-graphite-900/30" />
                </div>
                {/* Right: content */}
                <div className="p-6 sm:p-7 flex flex-col justify-between gap-5">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap mb-3">
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-magenta-500/30 bg-magenta-500/10 px-2.5 py-[3px] font-mono text-[9px] tracking-[0.18em] uppercase text-magenta-300">
                        <span className="w-1.5 h-1.5 rounded-full bg-magenta-300 animate-blink" />
                        Latest article
                      </span>
                      <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500">
                        {latest.date} · {latest.readTime}
                      </span>
                      {latest.tags?.slice(0, 2).map((t) => (
                        <span
                          key={t}
                          className="rounded-full border border-edge-700 bg-ink-900 px-2 py-[2px] font-mono text-[9px] uppercase tracking-[0.14em] text-zinc-400"
                        >
                          {t}
                        </span>
                      ))}
                      <AccessChip post={latest} />
                    </div>
                    <h3 className="font-display text-2xl sm:text-3xl text-white tracking-tight leading-tight group-hover:text-magenta-300 transition-colors">
                      {latest.title}
                    </h3>
                    <p className="mt-3 text-sm text-zinc-400 leading-relaxed max-w-2xl">
                      {latest.excerpt}
                    </p>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center gap-2 rounded-xl bg-magenta-500/10 border border-magenta-500/40 text-magenta-300 px-4 py-2 text-xs font-mono tracking-[0.14em] uppercase group-hover:bg-magenta-500 group-hover:text-ink-950 transition-colors">
                      Read article
                      <ArrowRight className="w-3 h-3" />
                    </span>
                    <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-zinc-500">
                      ~{latest.readTime} read
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          </FadeUp>
        )}

        {/* ── Blog banner CTA ── */}
        <FadeUp delay={0.14}>
          <Link
            to="/blog"
            data-testid="home-blog-banner"
            className="mt-6 group flex items-center justify-between gap-6 rounded-2xl border border-edge-700 bg-ink-900/60 px-6 py-5 hover:border-magenta-500/40 transition-colors"
          >
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl border border-edge-700 bg-graphite-900 flex items-center justify-center text-zinc-400 group-hover:text-magenta-300 transition-colors">
                <BookOpen className="w-5 h-5" />
              </div>
              <div>
                <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-magenta-300 mb-1">
                  The Journal
                </div>
                <div className="font-display text-lg sm:text-xl text-white tracking-tight">
                  Read the blog
                </div>
                <div className="mt-1 text-xs sm:text-sm text-zinc-500">
                  Essays and notes from the field, written by me.
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4 shrink-0">
              <span className="hidden sm:inline font-mono text-[10px] tracking-[0.18em] uppercase text-zinc-500">
                {count} {count === 1 ? "essay" : "essays"}
              </span>
              <div className="w-9 h-9 rounded-full border border-edge-700 flex items-center justify-center text-zinc-400 group-hover:text-magenta-300 group-hover:border-magenta-500/50 transition-colors">
                <ArrowRight className="w-4 h-4" />
              </div>
            </div>
          </Link>
        </FadeUp>
      </div>
    </section>
  );
};
